import { useState } from 'react';
import { InboxIcon, RefreshCwIcon, SendIcon } from 'lucide-react';
import { NotificationStatus } from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Field } from '@/components/field';
import { PageHeader } from '@/components/page-header';
import { PagerControls } from '@/components/pager';
import { useOffsetPager } from '@/hooks/use-offset-pager';
import { StatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorNotice, TableSkeleton, WarningNotice } from '@/components/states';
import { useAdminNotifications, useDlq, useRetryFailedReceipts } from '@/lib/admin-queries';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

/**
 * DLQ ko'rinishi (§4.5, §4.10).
 *
 * Ikki manba, chunki "yetib bormagan ish" ikki xil: chek tafsiloti vazifasi navbatda o'ladi,
 * xabar esa Telegramgacha yetmaydi. Ikkalasi bitta ekranda — operator ertalab bitta joyga
 * qaraydi.
 */
export function DlqPage() {
  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={strings.dlq.title} description={strings.dlq.subtitle} />
      <QueueFailures />
      <NotificationFailures />
    </section>
  );
}

const QUEUE_COLUMNS = 6;

function QueueFailures() {
  const pager = useOffsetPager();
  const dlq = useDlq({ page: pager.page, limit: pager.limit });
  const retryMutation = useRetryFailedReceipts();

  const rows = dlq.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{strings.dlq.queueHeading}</CardTitle>
        {dlq.data?.available ? (
          <CardDescription>
            {strings.dlq.total.replace('{total}', String(dlq.data.total))}
          </CardDescription>
        ) : null}
        <CardAction className="flex items-center gap-2">
          {dlq.data?.available && (dlq.data?.total ?? 0) > 0 ? (
            <Button
              size="sm"
              variant="outline"
              loading={retryMutation.isPending}
              onClick={() => retryMutation.mutate({ all: true })}
            >
              <RefreshCwIcon className="mr-1 size-3.5" />
              {strings.dlq.retryAllButton}
            </Button>
          ) : null}
          <Badge
            variant={(dlq.data?.total ?? 0) > 0 ? 'destructive' : 'secondary'}
            className="tabular"
          >
            {dlq.data?.total ?? 0}
          </Badge>
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {dlq.isError ? <ErrorNotice error={dlq.error} /> : null}

        {/* Navbat javob bermasa, bo'sh ro'yxat "xato yo'q" degani EMAS — buni ekran aytadi. */}
        {dlq.data && !dlq.data.available ? (
          <WarningNotice>{strings.dlq.unavailable}</WarningNotice>
        ) : null}

        {dlq.data?.available && rows.length === 0 ? (
          <EmptyState title={strings.dlq.empty} icon={InboxIcon} />
        ) : rows.length > 0 || dlq.isPending ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{strings.dlq.columnJob}</TableHead>
                <TableHead>{strings.dlq.columnReceipt}</TableHead>
                <TableHead>{strings.dlq.columnReason}</TableHead>
                <TableHead className="text-right">{strings.dlq.columnAttempts}</TableHead>
                <TableHead>{strings.dlq.columnFailedAt}</TableHead>
                <TableHead className="text-right">{strings.dlq.retryButton}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dlq.isPending ? <TableSkeleton columns={QUEUE_COLUMNS} /> : null}
              {rows.map((entry) => (
                <TableRow key={entry.jobId}>
                  <TableCell className="font-mono text-xs">{entry.jobId}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {entry.receiptId ?? strings.draws.noWinner}
                  </TableCell>
                  <TableCell className="text-destructive max-w-80 truncate">
                    {entry.reason}
                  </TableCell>
                  <TableCell className="tabular text-right">{entry.attempts}</TableCell>
                  <TableCell className="tabular whitespace-nowrap">
                    {formatTashkent(entry.failedAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    {entry.receiptId ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={retryMutation.isPending}
                        onClick={() => retryMutation.mutate({ receiptIds: [entry.receiptId!] })}
                      >
                        <RefreshCwIcon className="mr-1 size-3" />
                        {strings.dlq.retryButton}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}

        {/* Navbat ulanmagan bo'lsa sahifalash ma'nosiz: sanoq ham, sahifalar ham yo'q. */}
        {dlq.data?.available === false ? null : (
          <PagerControls
            pager={pager}
            total={dlq.data?.total}
            pageCount={dlq.data?.pageCount}
            rowCount={rows.length}
            busy={dlq.isFetching}
            label={strings.dlq.queueHeading}
          />
        )}
      </CardContent>
    </Card>
  );
}

const NOTIFICATION_COLUMNS = 6;

function NotificationFailures() {
  const pager = useOffsetPager();
  const [drawId, setDrawId] = useState('');
  // Ro'yxat FAQAT yetkazilmagan xabarlar uchun: yuborilganlari bu ekranga tegishli emas.
  const notifications = useAdminNotifications({
    status: NotificationStatus.FAILED,
    drawId: drawId.trim() || undefined,
    page: pager.page,
    limit: pager.limit,
  });

  const rows = notifications.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{strings.dlq.notificationsHeading}</CardTitle>
        <CardAction>
          <Badge
            variant={(notifications.data?.total ?? 0) > 0 ? 'destructive' : 'secondary'}
            className="tabular"
          >
            {notifications.data?.total ?? 0}
          </Badge>
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <Field label={strings.dlq.filterDraw} className="max-w-md">
          <Input
            name="drawId"
            className="font-mono"
            value={drawId}
            onChange={(event) => {
              // Filtr o'zgarishi sahifani boshiga qaytaradi — 4-sahifa yangi filtrda begona.
              pager.reset();
              setDrawId(event.target.value);
            }}
          />
        </Field>

        {notifications.isError ? <ErrorNotice error={notifications.error} /> : null}

        {notifications.isSuccess && rows.length === 0 ? (
          <EmptyState title={strings.dlq.notificationsEmpty} icon={SendIcon} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{strings.dlq.columnUser}</TableHead>
                <TableHead>{strings.dlq.columnType}</TableHead>
                <TableHead>{strings.dlq.columnStatus}</TableHead>
                <TableHead>{strings.dlq.columnDraw}</TableHead>
                <TableHead className="text-right">{strings.dlq.columnAttempts}</TableHead>
                <TableHead>{strings.dlq.columnReason}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notifications.isPending ? <TableSkeleton columns={NOTIFICATION_COLUMNS} /> : null}
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="tabular font-mono">{row.telegramId}</TableCell>
                  <TableCell className="font-mono text-xs">{row.type}</TableCell>
                  <TableCell>
                    <StatusBadge>{row.status}</StatusBadge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {row.drawId ?? strings.draws.noWinner}
                  </TableCell>
                  <TableCell className="tabular text-right">{row.attempts}</TableCell>
                  <TableCell className="text-destructive max-w-80 truncate text-xs">
                    {row.lastError ?? strings.draws.noWinner}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <PagerControls
          pager={pager}
          total={notifications.data?.total}
          pageCount={notifications.data?.pageCount}
          rowCount={rows.length}
          busy={notifications.isFetching}
          label={strings.dlq.notificationsHeading}
        />
      </CardContent>
    </Card>
  );
}
